import React, { useRef, useState, useEffect } from 'react';
import LanguageSwitcher from './LanguageSwitcher';
import { IoLogIn } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { IoHomeOutline, IoAdd, IoClose } from 'react-icons/io5';

const TabBar = ({ pdfs, activePdfId, onTabChange, onAddPdf, onCloseTab, isAuthenticated = true, onLogin }) => {
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [tabWidth, setTabWidth] = useState(200); // Default tab width
  const allTabsRef = useRef(null);
  const pdfTabsRef = useRef(null);
  const plusButtonRef = useRef(null);
  const { t } = useTranslation();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onAddPdf(file);
    }
  };

  const scrollTabs = (direction) => {
    if (pdfTabsRef.current) {
      pdfTabsRef.current.scrollBy({ left: direction * 100, behavior: 'smooth' });
    }
  };

  const handleTabChange = (id) => {
    console.log('Tab change requested for id:', id);
    if (id === null || pdfs.some(pdf => pdf.id === id)) {
      onTabChange(id);
    } else {
      console.error('Invalid PDF id:', id);
    }
  };

  useEffect(() => {
    const checkScroll = () => {
      if (allTabsRef.current && pdfTabsRef.current && plusButtonRef.current) {
        const containerWidth = allTabsRef.current.clientWidth;
        const tabsWidth = pdfTabsRef.current.scrollWidth;
        const plusButtonWidth = plusButtonRef.current.offsetWidth;
        const maxPlusButtonPosition = containerWidth - plusButtonWidth - 10; // 10px buffer

        setShowLeftScroll(pdfTabsRef.current.scrollLeft > 0);

        // Adjust tab width if necessary
        if (tabsWidth + plusButtonWidth > containerWidth) {
          const newTabWidth = Math.max(120, (containerWidth - plusButtonWidth - 50) / (pdfs.length + 1));
          setTabWidth(newTabWidth);
        } else {
          setTabWidth(200); // Reset to default width if there's enough space
        }

        // Position the plus button
        if (tabsWidth > maxPlusButtonPosition) {
          plusButtonRef.current.style.position = 'absolute';
          plusButtonRef.current.style.right = '10px';
        } else {
          plusButtonRef.current.style.position = 'static';
        }
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [pdfs]);

  return (
    <div ref={allTabsRef} className="flex items-end bg-[#0c0f14] px-1 pt-2 pb-0 w-full relative overflow-hidden select-none" style={{ minHeight: '40px' }}>
      {showLeftScroll && (
        <button
          className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-[#0c0f14]/90 backdrop-blur-sm text-gray-400 hover:text-white px-1.5 py-1 z-10 transition-colors duration-150"
          onClick={() => scrollTabs(-1)}
        >
          &lt;
        </button>
      )}
      <div
        ref={pdfTabsRef}
        className="flex items-end gap-px overflow-x-auto scrollbar-hide relative z-10"
      >
        {/* Home Tab */}
        <button
          className={`group relative px-3 py-1.5 flex-shrink-0 text-[13px] font-medium transition-all duration-200 rounded-t-lg
            ${activePdfId === null
              ? 'bg-[#161b22] text-gray-100 shadow-[0_-1px_0_0_rgba(99,102,241,0.5)]'
              : 'bg-transparent text-gray-400 hover:text-gray-200 hover:bg-[#161b22]/50'
            }`}
          onClick={() => handleTabChange(null)}
          style={{ minWidth: `${Math.min(tabWidth, 160)}px`, maxWidth: `${Math.min(tabWidth, 160)}px` }}
        >
          <span className="flex items-center justify-center gap-1.5">
            <IoHomeOutline className="text-sm flex-shrink-0" />
            <span>Home</span>
          </span>
          {activePdfId === null && (
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full" />
          )}
        </button>

        {/* PDF Tabs */}
        {pdfs.map((pdf) => (
          <button
            key={pdf.id}
            className={`group relative px-3 py-1.5 flex items-center justify-between flex-shrink-0 text-[13px] transition-all duration-200 rounded-t-lg
              ${pdf.id === activePdfId
                ? 'bg-[#161b22] text-gray-100 shadow-[0_-1px_0_0_rgba(56,189,248,0.3)]'
                : 'bg-transparent text-gray-400 hover:text-gray-200 hover:bg-[#161b22]/50'
              }`}
            onClick={() => handleTabChange(pdf.id)}
            style={{ minWidth: `${tabWidth}px`, maxWidth: `${tabWidth}px` }}
          >
            <span className="truncate flex-grow text-left font-medium">{pdf.name}</span>
            <span
              className="ml-2 flex-shrink-0 rounded-md p-0.5 text-gray-500 hover:text-gray-200 hover:bg-white/10 transition-all duration-150 opacity-0 group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onCloseTab(pdf.id);
              }}
              style={{ opacity: pdf.id === activePdfId ? 1 : undefined }}
            >
              <IoClose className="w-3.5 h-3.5" />
            </span>
            {pdf.id === activePdfId && (
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-sky-400 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Add PDF Button */}
      <label ref={plusButtonRef} className="cursor-pointer flex-shrink-0 ml-1 mb-0.5 p-1.5 rounded-md text-gray-400 hover:text-gray-200 hover:bg-[#161b22]/60 transition-all duration-150">
        <input
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={handleFileChange}
        />
        <IoAdd className="w-4 h-4" />
      </label>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Login Button */}
      {!isAuthenticated && (
        <button
          onClick={onLogin}
          className="mb-0.5 mr-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-3 py-1 rounded-md flex items-center gap-1.5 text-xs font-medium shadow-sm hover:shadow-md transition-all duration-200"
        >
          <IoLogIn className="text-sm" />
          <span>{t('common.login')}</span>
        </button>
      )}

      {/* Language Switcher */}
      <div className="mb-0.5 mr-2 relative z-50">
        <LanguageSwitcher />
      </div>
    </div>
  );
};

export default TabBar;
